import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, Loader2 } from "lucide-react";

export default function ImportModal({ open, onOpenChange, onImported }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drag, setDrag] = useState(false);

  const pick = (f) => {
    if (!f) return;
    setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/records/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.imported} kayıt içe aktarıldı`);
      onImported(data.columns);
      setFile(null);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="import-modal">
        <DialogHeader>
          <DialogTitle>Excel Verisi Yükle</DialogTitle>
          <DialogDescription>
            .xlsx, .xls veya .csv dosyası yükleyin. Kota yok — tüm satırlar
            aktarılır. Başlıklar mevcut sütunlarla eşleştirilir; yeni başlıklar
            otomatik sütun olarak eklenir.
          </DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            pick(e.dataTransfer.files?.[0]);
          }}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
            drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
          }`}
          data-testid="import-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => pick(e.target.files?.[0])}
            data-testid="import-file-input"
          />
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <FileSpreadsheet className="w-10 h-10 text-primary" />
              <span className="font-medium text-sm">{file.name}</span>
              <span className="text-xs text-muted-foreground">
                {(file.size / 1024).toFixed(1)} KB
              </span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <UploadCloud className="w-10 h-10" />
              <span className="text-sm">Dosyayı buraya sürükleyin veya tıklayın</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            İptal
          </Button>
          <Button onClick={upload} disabled={!file || loading} data-testid="import-confirm-button">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yükle"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
